const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    // Pydantic validation errors return detail as an array of objects
    if (Array.isArray(error.detail)) {
      const msg = error.detail.map(e => `${e.loc?.join('.')} — ${e.msg}`).join('; ');
      throw new Error(msg || 'Validation error');
    }
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

// Auth API
export const authAPI = {
  login: (email, password) =>
    fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    }).then(handleResponse),

  register: (data) =>
    fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(handleResponse),

  me: () =>
    fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  logout: () =>
    fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }),
};

// Users API
export const usersAPI = {
  getAll: () =>
    fetch(`${API_URL}/api/users`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  get: (userId) =>
    fetch(`${API_URL}/api/users/${userId}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  update: (userId, data) =>
    fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),
};

// Clients API
export const clientsAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/clients?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },

  get: (clientId) =>
    fetch(`${API_URL}/api/clients/${clientId}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/api/clients`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: (clientId, data) =>
    fetch(`${API_URL}/api/clients/${clientId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: (clientId) =>
    fetch(`${API_URL}/api/clients/${clientId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

// Cases API
export const casesAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/cases?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },

  get: (caseId) =>
    fetch(`${API_URL}/api/cases/${caseId}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/api/cases`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: (caseId, data) =>
    fetch(`${API_URL}/api/cases/${caseId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: (caseId) =>
    fetch(`${API_URL}/api/cases/${caseId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getCompliance: (caseId) =>
    fetch(`${API_URL}/api/cases/${caseId}/compliance`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  updateCompliance: (caseId, checklist) =>
    fetch(`${API_URL}/api/cases/${caseId}/compliance`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify({ checklist }),
    }).then(handleResponse),
};

// Tasks API
export const tasksAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/tasks?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },

  create: (data) =>
    fetch(`${API_URL}/api/tasks`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: (taskId, data) =>
    fetch(`${API_URL}/api/tasks/${taskId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: (taskId) =>
    fetch(`${API_URL}/api/tasks/${taskId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

// Documents API
export const documentsAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/documents?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },

  get: (documentId) =>
    fetch(`${API_URL}/api/documents/${documentId}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/api/documents`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: (documentId) =>
    fetch(`${API_URL}/api/documents/${documentId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () =>
    fetch(`${API_URL}/api/dashboard/stats`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getRevenue: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/dashboard/revenue?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },

  getForecast: () =>
    fetch(`${API_URL}/api/dashboard/forecast`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getRetention: () =>
    fetch(`${API_URL}/api/dashboard/retention`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getLeadAnalytics: () =>
    fetch(`${API_URL}/api/dashboard/lead-analytics`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/audit-logs?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },
};

// Commission Analytics API
export const commissionAPI = {
  getMonthly: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/commission/monthly?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },
  getAnalytics: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetch(`${API_URL}/api/commission/analytics?${searchParams}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse);
  },
};

// Analytics API
export const analyticsAPI = {
  getMortgageTypes: () =>
    fetch(`${API_URL}/api/analytics/mortgage-types`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getLenderUsage: () =>
    fetch(`${API_URL}/api/analytics/lender-usage`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

// Reports API
export const reportsAPI = {
  getCasesCompleted: (startDate, endDate) =>
    fetch(`${API_URL}/api/reports/cases-completed?start_date=${startDate}&end_date=${endDate}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
  getCommissionPaid: (startDate, endDate, reportType = 'commission') =>
    fetch(`${API_URL}/api/reports/commission-paid?start_date=${startDate}&end_date=${endDate}&report_type=${reportType}`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
  exportReport: (reportType, startDate, endDate, format = 'xlsx') => {
    return fetch(`${API_URL}/api/reports/export?report_type=${reportType}&start_date=${startDate}&end_date=${endDate}&format=${format}`, {
      credentials: 'include',
      headers: getHeaders(),
    });
  },
};

export default {
  auth: authAPI,
  users: usersAPI,
  clients: clientsAPI,
  cases: casesAPI,
  tasks: tasksAPI,
  documents: documentsAPI,
  dashboard: dashboardAPI,
  audit: auditAPI,
  commission: commissionAPI,
  analytics: analyticsAPI,
  reports: reportsAPI,
};

export const referralAPI = {
  getPartners: () =>
    fetch(`${API_URL}/api/referral-partners`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),

  getPartnerCases: (partnerName) =>
    fetch(`${API_URL}/api/referral-partners/${encodeURIComponent(partnerName)}/cases`, {
      credentials: 'include',
      headers: getHeaders(),
    }).then(handleResponse),
};

export const extractAPI = {
  extractClient: (formData) =>
    fetch(`${API_URL}/api/extract/client`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(handleResponse),
  extractCase: (formData) =>
    fetch(`${API_URL}/api/extract/case`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(handleResponse),
  extractSuitability: (formData) =>
    fetch(`${API_URL}/api/extract/suitability`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(handleResponse),
  extractCaseOverview: (formData) =>
    fetch(`${API_URL}/api/extract/case-overview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(handleResponse),
};
